import { parseCampaignParams } from "./queryStringUtils";

// Campaign attribution (utm_*, gclid and friends) is read from the URL when a
// quote flow is entered and sent in the `campaign` field of every quote
// submission, serialised as a compact JSON object so the backend column (a
// single string) needs no change.
//
// The value is held in sessionStorage so it survives the flow's `step=` query
// param rewrites and a refresh mid-flow. First value wins for the life of the
// tab: a later landing on a quote flow, with or without params, never replaces
// what was captured. This is deliberate for all three flows (direct, broker,
// referrer): the first landing is the one that brought the person here.

export const CAMPAIGN_STORAGE_KEY = "junoCampaignAttribution";
export const MAX_CAMPAIGN_LENGTH = 2000;

// Standard params first in a fixed order, then any other utm_* alphabetically,
// then the click IDs. When the serialised value would exceed
// MAX_CAMPAIGN_LENGTH, keys are dropped from the end of this order.
const PREFERRED_KEY_ORDER = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gclsrc",
  "gbraid",
  "wbraid",
];

function orderedKeys(params) {
  const known = PREFERRED_KEY_ORDER.filter((key) => key in params);
  const other = Object.keys(params)
    .filter((key) => !PREFERRED_KEY_ORDER.includes(key))
    .sort();
  const clickIds = known.filter((key) => !key.startsWith("utm_"));
  const knownUtm = known.filter((key) => key.startsWith("utm_"));
  return [...knownUtm, ...other, ...clickIds];
}

// Serialises campaign params to the string stored on the quote, or null when
// there is nothing to store. Never returns "{}".
export function serialiseCampaignParams(params) {
  if (!params) return null;
  const keys = orderedKeys(params);
  while (keys.length > 0) {
    const ordered = {};
    keys.forEach((key) => {
      ordered[key] = params[key];
    });
    const serialised = JSON.stringify(ordered);
    if (serialised.length <= MAX_CAMPAIGN_LENGTH) return serialised;
    keys.pop();
  }
  return null;
}

function readStored() {
  try {
    return window.sessionStorage.getItem(CAMPAIGN_STORAGE_KEY);
  } catch (err) {
    // sessionStorage can throw (e.g. Safari private mode, storage disabled)
    return null;
  }
}

function writeStored(value) {
  try {
    window.sessionStorage.setItem(CAMPAIGN_STORAGE_KEY, value);
  } catch (err) {
    // Fall through: the value is still returned to the caller for this render
  }
}

// Captures campaign params from the given React Router location (or the
// current URL) and returns the serialised value to send on quote submissions.
// Params already held for this session take precedence over the URL.
export function rememberCampaignAttribution(location = window.location) {
  const stored = readStored();
  if (stored) return stored;

  const serialised = serialiseCampaignParams(parseCampaignParams(location));
  if (serialised) writeStored(serialised);
  return serialised;
}
